import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { UserPermissionApiService } from './user-permission-api.service';
import { extractGroupedPermissionsPayload } from '../utils/permission-response.util';

/** Keep in sync with `AdminAuthStateService` token storage key (avoid injecting auth into this service — circular DI). */
const ADMIN_ACCESS_TOKEN_KEY = 'admin_access_token';

export interface UserPermissionsSnapshot {
  roles: string[];
  permissions: string[];
}

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly userPermissionApi = inject(UserPermissionApiService);

  private readonly subject = new BehaviorSubject<UserPermissionsSnapshot>({
    roles: [],
    permissions: [],
  });

  readonly userPermissions$ = this.subject.asObservable();

  /** Emits whenever bootstrap completes or permission slugs change (for impure pipes). */
  private readonly revision$ = new BehaviorSubject(0);
  readonly permissionsRevision$ = this.revision$.asObservable();

  private readonly permissionCache = new Map<string, boolean>();
  private readonly roleCache = new Map<string, boolean>();

  /** First `/permission/user` attempt finished (success or failure). Used to avoid hiding nav before bootstrap. */
  private readonly _bootstrapDone = signal(false);
  /** Reactive signal — templates reading this are automatically updated when bootstrap completes. */
  readonly bootstrapDone = this._bootstrapDone.asReadonly();

  /** Incremented whenever permission slugs change — use in templates/pipes for signal-based reactivity. */
  private readonly _permissionRevision = signal(0);
  readonly permissionRevision = this._permissionRevision.asReadonly();

  private inflight: Observable<void> | null = null;

  /**
   * Loads permission slugs from the grouped API payload (same shape as manage-permissions screen).
   */
  applyGroupedPermissions(data: Record<string, unknown> | null | undefined): void {
    const slugs: string[] = [];
    if (data && typeof data === 'object') {
      for (const rows of Object.values(data)) {
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
          if (row && typeof row === 'object' && typeof (row as { slug?: string }).slug === 'string') {
            slugs.push((row as { slug: string }).slug);
          }
        }
      }
    }
    this.subject.next({ roles: this.subject.value.roles, permissions: [...new Set(slugs)] });
    this.clearCache();
    this.revision$.next(this.revision$.value + 1);
    this._permissionRevision.update((n) => n + 1);
  }

  /**
   * Optional: hydrate role names when the login/profile payload includes nested `user_roles` (P2P-style).
   */
  applyRolesFromUserProfile(userData: unknown): void {
    const names: string[] = [];
    const u = userData as { user_roles?: unknown[] } | null | undefined;
    if (u?.user_roles && Array.isArray(u.user_roles)) {
      for (const ur of u.user_roles) {
        const role = (ur as { role?: { role_name?: string } })?.role;
        if (role?.role_name) names.push(role.role_name);
      }
    }
    this.subject.next({ roles: [...new Set(names)], permissions: this.subject.value.permissions });
    this.clearCache();
  }

  get initialPermissionFetchComplete(): boolean {
    return this._bootstrapDone();
  }

  can(slug: string): boolean {
    this._permissionRevision();
    if (!slug) return true;
    return this.hasPermission(slug);
  }

  canAny(slugs: string[]): boolean {
    this._permissionRevision();
    return slugs.some((s) => this.hasPermission(s));
  }

  hasPermission(slug: string): boolean {
    const key = `p:${slug}`;
    if (this.permissionCache.has(key)) return this.permissionCache.get(key)!;
    const ok = this.subject.value.permissions.includes(slug);
    this.permissionCache.set(key, ok);
    return ok;
  }

  hasAnyPermission(slugs: string[]): boolean {
    return slugs.some((s) => this.subject.value.permissions.includes(s));
  }

  hasAllPermissions(slugs: string[]): boolean {
    return slugs.every((s) => this.subject.value.permissions.includes(s));
  }

  hasRole(name: string): boolean {
    const key = `r:${name}`;
    if (this.roleCache.has(key)) return this.roleCache.get(key)!;
    const ok = this.subject.value.roles.includes(name);
    this.roleCache.set(key, ok);
    return ok;
  }

  hasAnyRole(names: string[]): boolean {
    return names.some((n) => this.subject.value.roles.includes(n));
  }

  hasAllRoles(names: string[]): boolean {
    return names.every((n) => this.subject.value.roles.includes(n));
  }

  getCurrentPermissions(): UserPermissionsSnapshot {
    return this.subject.value;
  }

  clearPermissions(): void {
    this.subject.next({ roles: [], permissions: [] });
    this._bootstrapDone.set(false);
    this.inflight = null;
    this.clearCache();
  }

  /**
   * Reset bootstrap state when a new auth session starts (login). Ensures the next
   * `ensureLoaded()` always refetches permissions for the new token.
   */
  resetForNewSession(): void {
    this._bootstrapDone.set(false);
    this.inflight = null;
    this.subject.next({ roles: [], permissions: [] });
    this.clearCache();
  }

  /**
   * Re-fetches permission slugs from the server (e.g. after role permissions change via WebSocket).
   */
  refreshPermissions(): Observable<void> {
    return this.userPermissionApi.fetchUserPermissionsGrouped().pipe(
      tap((res) => {
        this.applyGroupedPermissions(extractGroupedPermissionsPayload(res) ?? {});
      }),
      map(() => undefined),
    );
  }

  private finishBootstrap(res: unknown): void {
    const grouped = extractGroupedPermissionsPayload(res);
    this.applyGroupedPermissions(grouped ?? {});
    this._bootstrapDone.set(true);
    this.revision$.next(this.revision$.value + 1);
  }

  /**
   * Ensures `/permission/user` ran at least once for this session (shared in-flight observable).
   */
  ensureLoaded(): Observable<void> {
    if (!localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY)) {
      return of(undefined);
    }
    if (this._bootstrapDone()) {
      return of(undefined);
    }
    this.inflight ??= this.userPermissionApi.fetchUserPermissionsGrouped().pipe(
        tap((res) => this.finishBootstrap(res)),
        catchError(() => {
          this.applyGroupedPermissions({});
          this._bootstrapDone.set(true);
          this.revision$.next(this.revision$.value + 1);
          return of(undefined);
        }),
        map(() => undefined),
        finalize(() => {
          this.inflight = null;
        }),
        shareReplay(1),
      );
    return this.inflight;
  }

  private clearCache(): void {
    this.permissionCache.clear();
    this.roleCache.clear();
  }
}
